/* ============================================================
   AK Letters — pull-quote loader

   Runs on PDPs only (the section file is guarded by a Liquid
   {% if product != blank %}). Finds the section element by
   class, reads product id + filter settings from data attrs,
   then fetches reviews from Stamped's API and renders one
   random eligible review into the section.

   Moved out of an inline <script> inside the section file
   because Shopify's section rendering pipeline was truncating
   the closing </script> tag — the rendered HTML fused our
   script with the next <script> tag on the page, breaking the
   IIFE syntactically. External JS file sidesteps that entirely.

   Filter rules + endpoint shape verified live; see the section
   file's top comment for full context.
   ============================================================ */
(function () {
  if (window.__akLettersQuoteInit) return;
  window.__akLettersQuoteInit = true;

  function init() {
    var sectionEl = document.querySelector('.ak-letters-quote');
    if (!sectionEl) return;

    var productId = sectionEl.dataset.productId;
    var minRating = parseFloat(sectionEl.dataset.minRating) || 5;
    var minWords  = parseInt(sectionEl.dataset.minWords, 10) || 8;

    if (!productId) {
      hide(sectionEl);
      return;
    }

    pollForStamped(sectionEl, productId, minRating, minWords);
  }

  function hide(sectionEl) {
    sectionEl.style.display = 'none';
  }

  /* Title-case a single token. Handles hyphens and apostrophes so
     "jean-paul" → "Jean-Paul" and "o'brien" → "O'Brien". Lowercases
     the rest of each piece so all-caps input ("JEFF") also renders
     correctly. */
  function titleCaseToken(token) {
    if (!token) return token;
    return token.split(/([-'])/).map(function (piece) {
      if (piece === '-' || piece === "'") return piece;
      if (!piece) return piece;
      return piece.charAt(0).toUpperCase() + piece.slice(1).toLowerCase();
    }).join('');
  }

  function abbreviateName(raw) {
    if (!raw) return '';
    var parts = raw.trim().split(/\s+/);
    if (parts.length === 1) return titleCaseToken(parts[0]);
    /* "Jeff Smith" → "Jeff S.", "JEFF smith" → "Jeff S.",
       "jean-paul Smith" → "Jean-Paul S." — first name title-cased,
       last name reduced to a title-cased initial + period. */
    return titleCaseToken(parts[0])
      + ' '
      + titleCaseToken(parts[parts.length - 1]).charAt(0)
      + '.';
  }

  /* Truncate a customer letter at a sentence boundary so the
     pull-quote stays editorial rather than republishing the
     whole review. Rule:
       - <= 50 words: show in full, no expander
       - > 50 words: walk sentences from the start; accumulate
         until next sentence would push us past HARD_CAP (60),
         or we've already passed TARGET (45); stop at that
         boundary; everything after becomes the hidden remainder.
     Never chops mid-sentence — that looks careless on a
     luxury page. If a single sentence is longer than the cap
     (no boundaries to land on), we fall back to showing the
     whole thing with no expander rather than mid-sentence
     ellipsis. */
  function truncateForPullquote(body) {
    var TARGET_WORDS = 45;
    var HARD_CAP = 60;
    var SHORT_REVIEW_THRESHOLD = 50;

    var wordCount = body.split(/\s+/).filter(Boolean).length;
    if (wordCount <= SHORT_REVIEW_THRESHOLD) {
      return { visible: body, hidden: '', needsExpander: false };
    }

    // Match sentences ending in . ! or ? (followed by whitespace
    // or end of string). Trailing whitespace is captured inside
    // each match so concatenating later preserves spacing.
    var sentences = body.match(/[^.!?]+[.!?]+(\s+|$)/g);
    if (!sentences || sentences.length <= 1) {
      // No usable sentence boundary; refuse to chop.
      return { visible: body, hidden: '', needsExpander: false };
    }

    var visible = '';
    var visibleWords = 0;
    var cutIndex = sentences.length;

    for (var i = 0; i < sentences.length; i++) {
      var sentenceWords = sentences[i].trim().split(/\s+/).filter(Boolean).length;
      if (visible && visibleWords + sentenceWords > HARD_CAP) {
        cutIndex = i;
        break;
      }
      visible += sentences[i];
      visibleWords += sentenceWords;
      if (visibleWords >= TARGET_WORDS) {
        cutIndex = i + 1;
        break;
      }
    }

    var hidden = sentences.slice(cutIndex).join('').trim();
    visible = visible.trim();

    if (!hidden) {
      return { visible: body, hidden: '', needsExpander: false };
    }

    return { visible: visible, hidden: hidden, needsExpander: true };
  }

  function buildExpander(sectionEl) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ak-letters-quote__expand-trigger';
    btn.setAttribute('aria-expanded', 'false');

    var label = document.createElement('span');
    label.className = 'ak-letters-quote__expand-label';
    label.textContent = 'Read more';

    var chev = document.createElement('span');
    chev.className = 'ak-letters-quote__expand-chevron';
    chev.setAttribute('aria-hidden', 'true');
    chev.textContent = '⌄'; // ⌄ — quiet downward chevron, rotates 180° on expand

    btn.appendChild(label);
    btn.appendChild(chev);

    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        btn.setAttribute('aria-expanded', 'false');
        label.textContent = 'Read more';
        sectionEl.classList.remove('is-expanded');
      } else {
        btn.setAttribute('aria-expanded', 'true');
        label.textContent = 'Read less';
        sectionEl.classList.add('is-expanded');
      }
    });

    var wrap = document.createElement('p');
    wrap.className = 'ak-letters-quote__expand';
    wrap.appendChild(btn);
    return wrap;
  }

  function render(sectionEl, review) {
    var body = (review.body || '').trim();
    var name = (review.author || '').trim();
    var city = (review.location || '').trim();
    // Strip country / region suffixes so "Toronto, Ontario, Canada"
    // collapses to "Toronto". Keeps the byline tight. If the
    // reviewer only provided a country ("United States"), we leave
    // it as-is — better than nothing.
    if (city.indexOf(',') > -1) {
      city = city.split(',')[0].trim();
    }

    if (!body) { hide(sectionEl); return; }

    /* Render the letter into the <p data-quote-text>. For short
       reviews this is a plain textContent assignment. For long
       reviews we split into visible + hidden spans and inject an
       inline Read more / Read less expander between the
       blockquote and the figcaption (so the byline stays the
       letter's closer). The hidden span is display:none until
       .is-expanded is added to the section root. */
    var quoteTextEl = sectionEl.querySelector('[data-quote-text]');
    var figureEl = sectionEl.querySelector('[data-figure]');
    var blockquoteEl = sectionEl.querySelector('.ak-letters-quote__quote');
    var existingExpand = sectionEl.querySelector('.ak-letters-quote__expand');
    if (existingExpand) existingExpand.parentNode.removeChild(existingExpand);
    sectionEl.classList.remove('is-expanded');

    var parts = truncateForPullquote(body);
    if (parts.needsExpander) {
      quoteTextEl.textContent = '';
      var visibleSpan = document.createElement('span');
      visibleSpan.setAttribute('data-quote-visible', '');
      visibleSpan.textContent = parts.visible;
      var hiddenSpan = document.createElement('span');
      hiddenSpan.setAttribute('data-quote-hidden', '');
      hiddenSpan.className = 'ak-letters-quote__hidden';
      // Leading space so visible + hidden flow with one space
      // between them when the section expands.
      hiddenSpan.textContent = ' ' + parts.hidden;
      quoteTextEl.appendChild(visibleSpan);
      quoteTextEl.appendChild(hiddenSpan);

      var expander = buildExpander(sectionEl);
      // Insert between the blockquote and the figcaption so the
      // byline remains the closer of the letter.
      if (figureEl && blockquoteEl && blockquoteEl.nextSibling) {
        figureEl.insertBefore(expander, blockquoteEl.nextSibling);
      } else if (figureEl) {
        figureEl.appendChild(expander);
      }
    } else {
      quoteTextEl.textContent = body;
    }

    var nameEl = sectionEl.querySelector('[data-quote-name]');
    var cityEl = sectionEl.querySelector('[data-quote-city]');
    var sepEl  = sectionEl.querySelector('[data-quote-sep]');

    var displayName = abbreviateName(name);
    if (displayName) {
      nameEl.textContent = displayName;
    } else {
      nameEl.style.display = 'none';
    }

    if (city) {
      cityEl.textContent = city;
    } else {
      cityEl.style.display = 'none';
    }

    if (!displayName || !city) {
      sepEl.style.display = 'none';
    }

    sectionEl.classList.remove('is-loading');
    sectionEl.classList.add('is-ready');
  }

  function tryFetch(sectionEl, productId, minRating, minWords) {
    /* Stamped's globalOptions shape varies between theme installs.
       Older apps expose `sId` (numeric shop ID) + `storeName`;
       newer ones expose `storeUrl`. We need apiKey + ONE of the
       two store identifiers, and we build whichever URL variant
       matches what's available. */
    var opts = window.StampedGlobalOptions || {};
    var apiKey = opts.apiKey;
    var sId = opts.sId;
    var storeUrl = opts.storeUrl;
    if (!apiKey || (!storeUrl && !sId)) return false;

    var storeName = (window.Shopify && window.Shopify.shop) || window.location.host;
    var url = 'https://stamped.io/api/widget'
      + '?productId=' + encodeURIComponent(productId)
      + '&apiKey='    + encodeURIComponent(apiKey)
      + '&take=50';
    if (storeUrl) {
      url += '&storeUrl=' + encodeURIComponent(storeUrl);
    } else {
      url += '&sId=' + encodeURIComponent(sId)
          +  '&storeName=' + encodeURIComponent(storeName);
    }

    fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) return Promise.reject('HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        var html = json.widget || '';
        if (!html) { hide(sectionEl); return; }

        var parser = new DOMParser();
        var doc = parser.parseFromString('<body>' + html + '</body>', 'text/html');
        var cards = doc.querySelectorAll('.stamped-review');
        if (!cards.length) { hide(sectionEl); return; }

        /* Two-pool eligibility so the pull-quote prefers named
           voices but doesn't go dark on products whose review
           catalog is mostly anonymous.

             named[]     — author has a real name; renders with
                           the full "Ronald C., United States" byline,
                           which carries the editorial weight a
                           pull-quote needs.
             anonymous[] — author is blank or "Anonymous" / "Anon";
                           byline gets nullified at push time so
                           render() hides the name and the comma
                           separator. The quote then closes on either
                           location-only ("United States") or no byline
                           at all — both acceptable, neither hollow
                           the way "Anonymous, United States" reads.

           We pick randomly from named first. Only fall back to the
           anonymous pool when no named reviews are eligible. */
        var named = [];
        var anonymous = [];
        Array.prototype.forEach.call(cards, function (card) {
          var rating = parseFloat(card.getAttribute('data-rating') || '0');
          if (rating < minRating) return;

          // .stamped-review-body is a CONTAINER that includes the H3
          // title, the actual body paragraph, voting buttons, product
          // name, and "Was this helpful?" chrome. The clean body text
          // sits at .stamped-review-content-body (a child <p>). Using
          // the wrong selector earlier polluted the quote with
          // "Was this helpful? 0 0" trailing text.
          var bodyEl = card.querySelector('.stamped-review-content-body')
            || card.querySelector('.stamped-review-body p')
            || card.querySelector('.stamped-review-body');
          var body   = bodyEl ? bodyEl.textContent.trim() : '';
          if (!body) return;

          var wordCount = body.split(/\s+/).length;
          if (wordCount < minWords) return;

          var authorEl   = card.querySelector('.stamped-review-header .author, .author');
          var locationEl = card.querySelector('.stamped-review-header .review-location, .review-location');
          var author = authorEl ? authorEl.textContent.trim() : '';
          var location = locationEl ? locationEl.textContent.trim() : '';

          var nameLc = author.toLowerCase();
          var isAnon = !author || nameLc === 'anonymous' || nameLc === 'anon';

          if (isAnon) {
            // Drop the author at push time so render() suppresses
            // the name and the separator. Location stays.
            anonymous.push({ body: body, author: '', location: location });
          } else {
            named.push({ body: body, author: author, location: location });
          }
        });

        var pool = named.length > 0 ? named : anonymous;
        if (pool.length === 0) { hide(sectionEl); return; }

        var pick = pool[Math.floor(Math.random() * pool.length)];
        render(sectionEl, pick);
      })
      .catch(function (err) {
        console.warn('[ak-letters-quote] review fetch failed:', err);
        hide(sectionEl);
      });

    return true;
  }

  function pollForStamped(sectionEl, productId, minRating, minWords) {
    var attempts = 0;
    var maxAttempts = 20;

    function attempt() {
      if (tryFetch(sectionEl, productId, minRating, minWords)) return;
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(attempt, 500);
      } else {
        hide(sectionEl);
      }
    }
    attempt();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
